import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, Upload, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackForm = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/feedback-requests/by-token/${token}`);
        setInfo(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'This feedback link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/feedback-requests/by-token/${token}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhoto(res.data?.url || '');
      toast.success('Photo uploaded');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error('Please select a star rating');
      return;
    }
    if (!text.trim()) {
      toast.error('Please write a short feedback');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback-requests/by-token/${token}/submit`, {
        rating,
        testimonial_text: text.trim(),
        customer_photo: photo || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-gray-400" size={32} />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center">
            <AlertTriangle size={56} className="mx-auto mb-4 text-amber-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Unavailable</h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center" data-testid="feedback-thank-you">
            <CheckCircle2 size={64} className="mx-auto mb-5 text-green-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank you, {info?.customer_name?.split(' ')?.[0] || 'friend'}!</h1>
            <p className="text-gray-600 mb-1">
              Your feedback for <strong>{info?.project_name}</strong> has been received.
            </p>
            <p className="text-sm text-gray-500">
              Our team will review and may feature your story on our website.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <section className="flex-1 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-red-50 text-red-600 rounded-full mb-3">
                {info?.feedback_type || 'Feedback'}
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{info?.title || 'Share your experience'}</h1>
              <p className="text-gray-600">
                Hi <strong>{info?.customer_name}</strong>! We'd love to hear your honest experience with{' '}
                <strong>{info?.project_name}</strong>. Your feedback helps us serve you better.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="feedback-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating *</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHoverRating(n)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(n)}
                      className="p-1 hover:scale-110 transition-transform"
                      data-testid={`rating-star-${n}`}
                    >
                      <Star
                        size={36}
                        className={
                          (hoverRating || rating) >= n
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }
                      />
                    </button>
                  ))}
                  {rating > 0 && <span className="ml-3 text-sm text-gray-500">{rating} / 5</span>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Feedback *</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows="5"
                  required
                  placeholder="Tell us what you liked, what stood out, and anything we could improve..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="feedback-text"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                {photo ? (
                  <div className="relative inline-block">
                    <img src={photo} alt="Uploaded" className="max-h-48 rounded-lg border" />
                    <button
                      type="button"
                      onClick={() => setPhoto('')}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow text-red-600 hover:bg-red-50 text-xs px-2"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 transition-colors">
                      {uploadingPhoto ? (
                        <Loader2 size={24} className="mx-auto mb-2 animate-spin text-gray-400" />
                      ) : (
                        <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                      )}
                      <p className="text-sm text-gray-600">{uploadingPhoto ? 'Uploading...' : 'Click to upload a photo'}</p>
                      <p className="text-xs text-gray-400 mt-1">JPG / PNG, up to 5MB</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-60 text-white font-medium px-6 py-3 rounded-lg transition-all"
                data-testid="submit-feedback-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FeedbackForm;
